import { useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { Button } from "@platform/ui";

type LoginMethod = "email" | "mobile";
type AuthMode = "otp" | "password";

export function LoginScreen() {
  const [method, setMethod] = useState<LoginMethod>("email");
  const [authMode, setAuthMode] = useState<AuthMode>("otp");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  function changeMethod(nextMethod: LoginMethod) {
    setMethod(nextMethod);
    setIdentifier("");
    setOtp("");
    setOtpSent(false);
  }

  function changeAuthMode(nextMode: AuthMode) {
    setAuthMode(nextMode);
    setPassword("");
    setOtp("");
    setOtpSent(false);
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={styles.screen}
    >
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.card}>
          <View style={styles.brandMark}>
            <Text style={styles.brandMarkText}>A</Text>
          </View>
          <Text style={styles.title}>Welcome back</Text>
          <Text style={styles.subtitle}>Sign in to continue to your account.</Text>

          <View accessibilityRole="tablist" style={styles.switcher}>
            <MethodButton
              active={method === "email"}
              label="Email"
              onPress={() => changeMethod("email")}
            />
            <MethodButton
              active={method === "mobile"}
              label="Mobile number"
              onPress={() => changeMethod("mobile")}
            />
          </View>

          <View style={styles.form}>
            <Text style={styles.label}>{method === "email" ? "Email address" : "Mobile number"}</Text>
            <TextInput
              autoCapitalize="none"
              autoComplete={method === "email" ? "email" : "tel"}
              keyboardType={method === "email" ? "email-address" : "phone-pad"}
              placeholder={method === "email" ? "you@example.com" : "+1 555 123 4567"}
              placeholderTextColor="#8b9793"
              style={styles.input}
              value={identifier}
              onChangeText={setIdentifier}
            />

            <View accessibilityRole="tablist" style={styles.authSwitcher}>
              <MethodButton
                active={authMode === "otp"}
                label="Use OTP"
                onPress={() => changeAuthMode("otp")}
              />
              <MethodButton
                active={authMode === "password"}
                label="Use password"
                onPress={() => changeAuthMode("password")}
              />
            </View>

            {authMode === "password" ? (
              <>
                <View style={styles.passwordHeading}>
                  <Text style={styles.label}>Password</Text>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => undefined}
                    style={({ pressed }) => [styles.linkButton, pressed && styles.linkPressed]}
                  >
                    <Text style={styles.link}>Forgot password?</Text>
                  </Pressable>
                </View>
                <TextInput
                  autoComplete="password"
                  placeholder="Enter your password"
                  placeholderTextColor="#8b9793"
                  secureTextEntry
                  style={styles.input}
                  value={password}
                  onChangeText={setPassword}
                />
                <Button label="Log in" onPress={() => undefined} style={styles.loginButton} />
              </>
            ) : (
              <>
                {otpSent && (
                  <TextInput
                    autoComplete="one-time-code"
                    keyboardType="number-pad"
                    placeholder="Enter the 6-digit OTP"
                    placeholderTextColor="#8b9793"
                    style={styles.input}
                    value={otp}
                    onChangeText={setOtp}
                  />
                )}
                <Button
                  label={otpSent ? "Verify OTP" : "Generate OTP"}
                  onPress={() => setOtpSent(true)}
                  style={styles.loginButton}
                />
              </>
            )}
          </View>

          <View style={styles.signupRow}>
            <Text style={styles.signupPrompt}>Don't have an account?</Text>
            <Pressable
              accessibilityRole="button"
              onPress={() => undefined}
              style={({ pressed }) => [styles.linkButton, pressed && styles.linkPressed]}
            >
              <Text style={styles.link}>Sign up</Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

type MethodButtonProps = {
  active: boolean;
  label: string;
  onPress: () => void;
};

function MethodButton({ active, label, onPress }: MethodButtonProps) {
  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      style={({ pressed }) => [
        styles.method,
        active && styles.methodActive,
        pressed && styles.methodPressed,
      ]}
    >
      <Text style={[styles.methodText, active && styles.methodTextActive]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  screen: { backgroundColor: "#111214", flex: 1 },
  content: { alignItems: "center", flexGrow: 1, justifyContent: "center", padding: 24 },
  card: {
    backgroundColor: "#292a2c", borderColor: "#4a4d50", borderRadius: 8, borderWidth: 1,
    elevation: 8, maxWidth: 440, padding: 32, boxShadow: "0px 18px 42px rgba(0, 0, 0, 0.55), 0px 0px 0px 1px rgba(220, 225, 228, 0.2)", width: "100%",
  },
  brandMark: {
    alignItems: "center", alignSelf: "center", backgroundColor: "#ff3d9a", borderRadius: 6,
    height: 52, justifyContent: "center", marginBottom: 22, transform: [{ rotate: "-5deg" }], width: 52,
  },
  brandMarkText: { color: "#111214", fontSize: 26, fontWeight: "800", transform: [{ rotate: "5deg" }] },
  title: { color: "#f2f4f5", fontSize: 30, fontWeight: "700", textAlign: "center" },
  subtitle: { color: "#a5aaad", fontSize: 15, marginTop: 8, textAlign: "center" },
  switcher: { backgroundColor: "#1a1b1d", borderColor: "#4a4d50", borderRadius: 4, borderWidth: 1, flexDirection: "row", gap: 4, marginTop: 28, padding: 4 },
  authSwitcher: { backgroundColor: "#1a1b1d", borderColor: "#4a4d50", borderRadius: 4, borderWidth: 1, flexDirection: "row", gap: 4, marginBottom: 18, marginTop: 20, padding: 4 },
  method: { alignItems: "center", borderRadius: 3, flex: 1, justifyContent: "center", minHeight: 40, paddingHorizontal: 10 },
  methodActive: { backgroundColor: "#484a4d", boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.35), 0px 0px 0px 1px rgba(220, 225, 228, 0.25)", elevation: 2 },
  methodPressed: { opacity: 0.72, transform: [{ scale: 0.98 }] },
  methodText: { color: "#a5aaad", fontSize: 13, fontWeight: "600" },
  methodTextActive: { color: "#28e0ca" },
  form: { marginTop: 24 },
  label: { color: "#f2f4f5", fontSize: 13, fontWeight: "600", marginBottom: 8 },
  input: { backgroundColor: "#1a1b1d", borderColor: "#4a4d50", borderRadius: 4, borderWidth: 1, color: "#f2f4f5", fontSize: 15, minHeight: 48, paddingHorizontal: 14 },
  passwordHeading: { alignItems: "center", flexDirection: "row", justifyContent: "space-between", marginTop: 18 },
  linkButton: { paddingVertical: 4 },
  linkPressed: { opacity: 0.55, transform: [{ translateY: 1 }] },
  link: { color: "#28e0ca", fontSize: 13, fontWeight: "600" },
  loginButton: { marginTop: 24 },
  signupRow: { alignItems: "center", flexDirection: "row", gap: 5, justifyContent: "center", marginTop: 26 },
  signupPrompt: { color: "#a5aaad", fontSize: 13 },
});